/*
 * Consts.
 */

import {useCallback, useMemo} from 'react';

import {buildInboxSearchUrl} from '../../helpers/models/inboxHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ChannelModel} from '../../models/api/channelModel';
import {ApiInbox, InboxModel} from '../../models/api/inboxModel';
import {ApiSearchInboxes} from '../../models/api/searchInboxModel';
import {fetchApiAsync} from '../../services/http/http';
import {makeGetInbox} from '../../store/data/inboxes/inboxesSelectors';
import {makeResourceListPaginationOffset} from '../helpers/resourceListPagination/resourceListPaginationOffset';
import {useResourceList} from '../helpers/resourceTableHooks';
import {
  SettingsInvalidateQueriesOptions,
  useSettingsInvalidateQueries,
  useSettingsQuery,
  UseSettingsQueryOptions,
} from '../helpers/useSettingsQuery';
import {useQueryDebounce} from './fetcherHelpers';

/*
 * Consts.
 */

const inboxQueryKeyFactory = {
  id: (inboxId: number) => ['inboxSettings', {id: inboxId}],
  list: (query: string, namespace: string) => ['inboxesList', {query, namespace}],
};

/*
 * Fetchers.
 */

export function useFetchInbox(inboxId: number | undefined = 0, options?: UseSettingsQueryOptions) {
  const getInbox = useMemo(() => makeGetInbox(() => inboxId), [inboxId]);
  const inboxFromStore = useAppSelector(getInbox);
  const {data, asyncStatus} = useSettingsQuery(
    inboxQueryKeyFactory.id(inboxId),
    async ({company, cancelToken}) => {
      if (!inboxId) {
        return undefined;
      }
      if (inboxFromStore) {
        return inboxFromStore;
      }
      return fetchApiAsync(ApiInbox, `${company.url}/inboxes/${inboxId}`, {cancelToken});
    },
    options,
  );
  return {inbox: data, status: asyncStatus};
}

export function useInvalidateInbox() {
  const invalidate = useSettingsInvalidateQueries();

  return useCallback(
    (inboxId: number, options?: SettingsInvalidateQueriesOptions) => {
      invalidate(inboxQueryKeyFactory.id(inboxId), options);
    },
    [invalidate],
  );
}

export interface InboxWithChannelsModel {
  inbox: InboxModel;
  channels: ReadonlyArray<ChannelModel>;
}

export function useFetchInboxesAndChannels(query: string, namespace: string) {
  const debouncedQuery = useQueryDebounce(query);
  const queryKey = inboxQueryKeyFactory.list(debouncedQuery, namespace);

  const pagination = useMemo(
    () =>
      makeResourceListPaginationOffset<InboxWithChannelsModel>({
        fetchApi: async ({limit, offset}, getNextOffset, company, cancelToken) => {
          const url = buildInboxSearchUrl(company.url, debouncedQuery, {
            limit,
            offset,
            namespace,
            shouldIncludePrivate: true,
            shouldIncludeChannels: true,
          });
          const results = await fetchApiAsync(ApiSearchInboxes, url, {cancelToken});
          const {inboxes, channels} = results;
          const items = inboxes.map((inbox) => ({
            inbox,
            channels: channels.filter((c) => c.inboxId === inbox.id),
          }));

          return {
            items,
            nextOffset: getNextOffset(offset),
          };
        },
      }),
    [debouncedQuery, namespace],
  );

  const inboxesResourceList = useResourceList(queryKey, pagination);

  return {
    items: inboxesResourceList.items,
    hasError: inboxesResourceList.hasError,
    loadMore: inboxesResourceList.loadMore,
    moreStatus: inboxesResourceList.moreStatus,
  };
}
